import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult
} from "@azure/msal-browser";

const armScopes = ["https://management.azure.com/user_impersonation"];
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID?.trim();
let clientPromise: Promise<PublicClientApplication> | null = null;

function normalizeTenantId(value: string): string {
  return value.trim().toLowerCase();
}

function resultMatchesTenant(result: AuthenticationResult, tenantId: string): boolean {
  return normalizeTenantId(result.tenantId) === normalizeTenantId(tenantId);
}

function requiresInteraction(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) {
    return true;
  }

  const errorCode =
    typeof error === "object" && error && "errorCode" in error
      ? String(error.errorCode).toLowerCase()
      : "";

  return ["interaction_required", "consent_required", "login_required", "no_tokens_found"].some((code) =>
    errorCode.includes(code)
  );
}

function getAuthCallbackUri(): string {
  return `${window.location.origin}/auth-callback.html`;
}

function requireClientId(): string {
  if (!clientId) {
    throw new Error("Microsoft login is not configured. Set VITE_ENTRA_CLIENT_ID before building the app.");
  }

  return clientId;
}

async function getClient(): Promise<PublicClientApplication> {
  if (!clientPromise) {
    clientPromise = (async () => {
      const client = new PublicClientApplication({
        auth: {
          clientId: requireClientId(),
          authority: "https://login.microsoftonline.com/common",
          redirectUri: getAuthCallbackUri(),
          postLogoutRedirectUri: window.location.origin
        },
        cache: {
          cacheLocation: "sessionStorage"
        }
      });

      await client.initialize();
      return client;
    })();
  }

  return clientPromise;
}

function getActiveAccount(client: PublicClientApplication): AccountInfo {
  const account = client.getActiveAccount() || client.getAllAccounts()[0];

  if (!account) {
    throw new Error("No Microsoft account is signed in.");
  }

  client.setActiveAccount(account);
  return account;
}

function getTenantAccount(
  client: PublicClientApplication,
  activeAccount: AccountInfo,
  tenantId: string
): AccountInfo {
  return (
    client.getAccount({
      homeAccountId: activeAccount.homeAccountId,
      tenantId
    }) || activeAccount
  );
}

function activateResultAccount(
  client: PublicClientApplication,
  result: AuthenticationResult,
  tenantId: string
): AuthenticationResult {
  if (!resultMatchesTenant(result, tenantId)) {
    throw new Error(
      "Microsoft returned a token for a different Azure tenant. Select the same Microsoft account in the popup, or sign out and reconnect it."
    );
  }

  client.setActiveAccount(result.account);
  return result;
}

export function isMicrosoftLoginConfigured(): boolean {
  return Boolean(clientId);
}

export async function signInWithMicrosoft(): Promise<AuthenticationResult> {
  const client = await getClient();
  const result = await client.loginPopup({
    scopes: armScopes,
    prompt: "select_account",
    redirectUri: getAuthCallbackUri()
  });

  client.setActiveAccount(result.account);
  return result;
}

export async function acquireMicrosoftTenantToken(tenantId: string): Promise<AuthenticationResult> {
  const client = await getClient();
  const activeAccount = getActiveAccount(client);
  const account = getTenantAccount(client, activeAccount, tenantId);
  const request = {
    account,
    authority: `https://login.microsoftonline.com/${encodeURIComponent(tenantId)}`,
    scopes: armScopes,
    redirectUri: getAuthCallbackUri(),
    forceRefresh: true
  };

  try {
    const result = await client.acquireTokenSilent(request);

    if (resultMatchesTenant(result, tenantId)) {
      return activateResultAccount(client, result, tenantId);
    }
  } catch (error) {
    if (!requiresInteraction(error)) {
      throw error;
    }
  }

  const result = await client.acquireTokenPopup({
    account,
    authority: request.authority,
    scopes: armScopes,
    redirectUri: request.redirectUri,
    prompt: "select_account"
  });

  return activateResultAccount(client, result, tenantId);
}

export async function signOutMicrosoft(): Promise<void> {
  const client = await getClient();
  const account = client.getActiveAccount() || client.getAllAccounts()[0];

  if (!account) {
    window.location.assign("/");
    return;
  }

  await client.logoutRedirect({
    account,
    postLogoutRedirectUri: window.location.origin
  });
}
